// ============================================
// Game Loader — Incremental save/load restore
// Extracted from main.js
// ============================================

import { storyManager } from '../engine/story.js';
import { variableManager } from '../engine/variables.js';
import { tagHandler } from '../engine/tags.js';
import { events, Events } from '../engine/events.js';
import { inventoryManager } from './inventory.js';
import { achievementManager } from './achievements.js';
import { statisticsTracker } from './statistics.js';
import { L } from './localization.js';

import { statsUI } from '../ui/stats.js';
import { wardrobeUI } from '../ui/wardrobe.js';
import { shopUI } from '../ui/shop.js';
import { phoneUI } from '../ui/phone.js';
import { characterPanelUI } from '../ui/character-panel.js';
import { galleryUI } from '../ui/gallery.js';

/**
 * Undo-state providers — JS-manager state that the undo snapshot's
 * inkState + variableManager pair does NOT cover. Inventory mutates via
 * #item: tags / shop purchases, phone via #sms: tags / app installs; undo
 * across any of those left the managers in the future while Ink rewound
 * (cage "resurrected", chastity_flag_no_tier — foundby:Diadan 0.3.0.3).
 * Registered on BOTH boot paths (main.js bootAndApplySave, character-editor
 * new game); registration is keyed, so re-runs replace instead of stacking.
 * Deep-copy via JSON — both payloads are plain serializable data.
 */
export function registerUndoStateProviders() {
    const deepCopy = (x) => (x == null ? null : JSON.parse(JSON.stringify(x)));
    storyManager.registerUndoStateProvider({
        key: 'inventory',
        capture: () => deepCopy(inventoryManager.getSerializableState()),
        restore: (s) => {
            if (!s) return;
            inventoryManager.restoreFromSave(s.ownedTags || [], s.equippedSlots || [], s.equippedTags || []);
            inventoryManager.restoreSavedOutfit(s.savedOutfit || null);
            const story = storyManager.currentStory;
            if (story) inventoryManager.syncFromInk(story);
            inventoryManager.recalculateAllBuffs();
            try {
                wardrobeUI.updateInventory(inventoryManager.getOwnedItemObjects());
                wardrobeUI.updateEquipped(inventoryManager.getEquippedMap());
                characterPanelUI.updateSlots();
            } catch (e) { /* UI may not be mounted yet */ }
        },
    });
    storyManager.registerUndoStateProvider({
        key: 'phone',
        capture: () => deepCopy(phoneUI.serialize?.() || null),
        restore: (s) => { if (s) phoneUI.deserialize(s); },
    });
}

/**
 * Restore game state from save data without full page reload.
 * @param {object} data - Save data (inkState, baseStats, ownedItemTags, etc.)
 * @param {object} opts - { setCharacterData }
 */
export function loadGameIncremental(data, opts = {}) {
    try {
        const story = storyManager.currentStory;
        if (!story) {
            console.error('[PnO] loadGameIncremental: no story');
            if (statsUI?.showToast) statsUI.showToast('Save data incomplete — please start a New Game', 'notif-error');
            return;
        }

        // Fresh pointer-recovery budget for this load. If the restored Ink
        // state carries an invalid callstack pointer (old save into a
        // recompiled story), StoryManager re-enters the saved location once to
        // rebuild a saveable state — see story.js _processStoryContent catch.
        storyManager._pointerRecovered = false;

        const isRecovery = !data.inkState;

        // 1. Restore Ink story state (skip for recovery saves — will jump to Apartment)
        if (data.inkState) {
            try { story.state.LoadJson(data.inkState); }
            catch(e) { console.error('[PnO] Step 1 (LoadJson) failed:', e); throw e; }
        } else {
            console.log('[PnO] Recovery save detected — stats will be restored, story jumps to Apartment');
        }

        // 1.5. Pointer-validity probe (foundby:ScarletMemories, 0.2.7 -> 0.2.7.1,
        // slot 4). An old save loaded into a recompiled story can carry a
        // callstack pointer that no longer resolves: the state still RENDERS,
        // but story.state.toJson() throws "previousPointer.Resolve() is null",
        // so every auto-save fails ("Slot N did not save") and the run is
        // effectively dead. Probe with the same toJson() the save path uses; if
        // it throws, re-enter the saved location to rebuild a clean, saveable
        // callstack — keeps stats/inventory/vars, loses only the exact mid-scene
        // position. (Her manual workaround was load -> go outside -> re-save,
        // which is precisely this reset.)
        if (data.inkState) {
            try {
                story.state.toJson();
            } catch (probeErr) {
                const repairLoc = data.currentLocation || 'Apartment';
                console.warn(`[PnO] Save state not serialisable after update (${probeErr?.message}) — repairing by re-entering ${repairLoc}`);
                try {
                    storyManager._abortForcedEvent?.(true);
                    story.ChoosePathString(repairLoc);
                    if (statsUI?.showToast) statsUI.showToast('Save repaired after the update. You may need to re-open the current scene.', 'notif-info');
                } catch (repairErr) {
                    console.error('[PnO] Pointer repair failed, falling back to Apartment:', repairErr?.message);
                    try { story.ChoosePathString('Apartment'); } catch (e) {}
                }
            }
        }

        // 2. Restore variables
        try {
            if (data.baseStats) {
                variableManager.importState({
                    baseVar: data.baseStats,
                    buffedVar: data.buffedStats || {},
                    flags: data.flags || {},
                });
            }
        } catch(e) { console.error('[PnO] Step 2 (importState) failed:', e); throw e; }

        // 2a. Heal Allure / Comfort base — equipment-only stats whose baseVar
        // must stay 0. Pre-fix saves accumulated buff into baseVar; reset
        // here so recalculateAllBuffs in Step 3 sees a clean slate.
        try {
            variableManager.baseVar.set('Allure', 0);
            variableManager.baseVar.set('Comfort', 0);
        } catch(e) { /* baseVar always exists, but be defensive */ }

        // 2b. Overwrite baseVar/flags with values from the just-loaded inkState.
        // The save's JS shadow can drift from Ink during refreshView's blocked-
        // observer window (e.g. is_dirty cleared in overnight processing didn't
        // propagate to flags map; the save then captured the stale shadow).
        // Ink is the source of truth on a normal load, so re-pull every observed
        // var. Recovery saves (no inkState) still use the imported shadow as
        // the only available source.
        if (!isRecovery) {
            try { variableManager.syncFromInk(); }
            catch(e) { console.warn('[PnO] Step 2b (syncFromInk) non-fatal:', e?.message); }
        }

        // 3. Restore inventory
        try {
            // F20: auto-repair equipment array length mismatch instead of crashing.
            // buildSaveData always emits balanced arrays from a Map iterator, so a
            // mismatch implies external corruption — clip to min length and log.
            if (Array.isArray(data.equippedSlots) && Array.isArray(data.equippedItemTags) &&
                data.equippedSlots.length !== data.equippedItemTags.length) {
                const minLen = Math.min(data.equippedSlots.length, data.equippedItemTags.length);
                console.warn(`[PnO] Auto-repaired equipment array mismatch: slots=${data.equippedSlots.length} tags=${data.equippedItemTags.length} → ${minLen}`);
                data.equippedSlots = data.equippedSlots.slice(0, minLen);
                data.equippedItemTags = data.equippedItemTags.slice(0, minLen);
            }

            if (data.ownedItemTags) {
                inventoryManager.restoreFromSave(
                    data.ownedItemTags,
                    data.equippedSlots || [],
                    data.equippedItemTags || [],
                );
                inventoryManager.restoreSavedOutfit(data.savedOutfit || null);

                // §49 wardrobe-empty recovery (0.2.9.0.4, foundby:VPS player
                // Day-50 iOS 18.7 Safari). Two failure modes:
                //  B) The save STILL has owned tags but the item DB failed to
                //     load at restore time, so restoreFromSave dropped every tag
                //     (_itemDb.has() === false for all). Re-fetch the DB and
                //     re-restore once so the clothes come back. loadGameIncremental
                //     is sync, so this is a fire-and-forget self-heal; the autosave
                //     guard in save.js prevents the empty state from being
                //     persisted in the meantime.
                //  C) The save was ALREADY emptied by a prior autosave overwrite
                //     (clothes unrecoverable; regular clothing has no Ink
                //     ownership var). On a Day>1 save that loads with a totally
                //     empty wardrobe, re-grant the starter set so the player is
                //     not trapped with nothing to wear. Gated to Day>1 + fully
                //     empty so a deliberately all-discarded wardrobe is the only
                //     false-positive surface.
                const _savedTagCount = Array.isArray(data.ownedItemTags) ? data.ownedItemTags.length : 0;
                const _restoredCount = inventoryManager.getOwnedItemObjects().length;
                if (_savedTagCount > 0 && _restoredCount < _savedTagCount) {
                    // Mode B, hardened 0.3.0.7: fire on ANY drop (partial OR total),
                    // not just a full wipe. A cold/partial item DB at restore time
                    // drops the tags it can't resolve (inventory.js:1358); re-fetch
                    // the DB and re-restore once so every garment comes back.
                    console.warn(`[PnO] Inventory restore dropped ${_savedTagCount - _restoredCount}/${_savedTagCount} owned tags (item DB empty/partial?), re-fetching DB to recover`);
                    inventoryManager.loadItemDatabase().then(() => {
                        try {
                            inventoryManager.restoreFromSave(
                                data.ownedItemTags,
                                data.equippedSlots || [],
                                data.equippedItemTags || [],
                            );
                            inventoryManager.restoreSavedOutfit(data.savedOutfit || null);
                            inventoryManager.syncFromInk(story);
                            inventoryManager.recalculateAllBuffs();
                            try {
                                wardrobeUI.updateInventory(inventoryManager.getOwnedItemObjects());
                                wardrobeUI.updateEquipped(inventoryManager.getEquippedMap());
                            } catch(e) { /* UI may not be mounted yet */ }
                            console.log(`[PnO] Inventory recovered after DB re-fetch: ${inventoryManager.getOwnedItemObjects().length} owned`);
                        } catch(e) { console.warn('[PnO] Inventory re-restore after DB re-fetch failed:', e?.message); }
                    }).catch(e => console.warn('[PnO] DB re-fetch for inventory recovery failed:', e?.message));
                } else if (_savedTagCount === 0 && _restoredCount === 0) {
                    // Mode C: only un-brick a real (Day>1) run, never a fresh start
                    let _curDay = 1;
                    try { const d = story.variablesState['Day']; if (typeof d === 'number') _curDay = d; } catch(e) {}
                    if (_curDay > 1) {
                        console.warn(`[PnO] Day ${_curDay} save loaded with an empty wardrobe, re-granting starter items (clothes were lost to the §49 autosave gap)`);
                        // giveStarterItems is async only if it must re-fetch the DB;
                        // wrap in Promise.resolve so the UI refresh works whether it
                        // resolved synchronously (DB already loaded) or not.
                        Promise.resolve(inventoryManager.giveStarterItems()).then(() => {
                            try {
                                wardrobeUI.updateInventory(inventoryManager.getOwnedItemObjects());
                                wardrobeUI.updateEquipped(inventoryManager.getEquippedMap());
                            } catch(e) { /* UI may not be mounted yet */ }
                        }).catch(e => console.warn('[PnO] Starter re-grant failed:', e?.message));
                    }
                }
            }
            // F01: Recovery-Save protection. Without inkState, story.variablesState
            // contains Ink defaults (false / 0). inventory.syncFromInk's reverse-
            // remove path then deletes every owned item because Ink says "not
            // owned". Backfill ownership VARs from the imported flags map BEFORE
            // syncFromInk reads them. Normal loads already restored Ink via
            // LoadJson in Step 1 so this branch is recovery-only.
            if (isRecovery && data.flags) {
                for (const [k, v] of Object.entries(data.flags)) {
                    if (k.startsWith('owns_')) {
                        try { story.variablesState[k] = !!v; } catch(e) {}
                    }
                }
            }
            // 3b. Sync Ink ownership vars → inventory (items granted via Ink but missing from save)
            inventoryManager.syncFromInk(story);
            inventoryManager.recalculateAllBuffs();

            // 3c. Re-derive Ink vars from owned items (fixes saves from before setsOnPurchase)
            for (const ownedTag of (data.ownedItemTags || [])) {
                const ownedItem = inventoryManager.getItemByTag?.(ownedTag);
                if (ownedItem?.setsOnPurchase) {
                    for (const [varName, value] of Object.entries(ownedItem.setsOnPurchase)) {
                        try {
                            const current = story.variablesState[varName];
                            // Only set if still at default (don't overwrite correct progression)
                            if (current === false || current === 0) {
                                story.variablesState[varName] = value;
                            }
                        } catch(e) {}
                    }
                }
            }
        } catch(e) { console.error('[PnO] Step 3 (inventory) failed:', e); throw e; }

        // 4. Restore SMS
        if (data.smsMessages) {
            let threads = data.smsMessages;
            // Convert flat array [{npcId, text, ...}] to grouped {npcId: [msgs]} if needed
            if (Array.isArray(threads)) {
                const grouped = {};
                for (const msg of threads) {
                    const id = msg.npcId || msg.conversationId || 'unknown';
                    if (!grouped[id]) grouped[id] = [];
                    grouped[id].push(msg);
                }
                threads = grouped;
            }
            phoneUI.deserialize({ threads, unread: [] });
        }

        // 5. Restore achievements
        if (data.unlockedAchievements) {
            // G2 fix (audit 2026-05-24): method is restoreFromSave, the old
            // restoreUnlocked?. silently no-op'd on imported/cross-device saves.
            achievementManager.restoreFromSave?.(data.unlockedAchievements);
        }

        // 5b. Restore gallery unlocks (UNION — never clears the local gallery).
        // The gallery lives in localStorage['PnO_Gallery'] and is global across
        // saves, so it isn't part of the inkState snapshot. Merging the save's
        // list in makes it portable across devices (export on PC A → import on
        // PC B reinstates the unlocked grid). deserialize() only .add()s names,
        // so a device that already has a fuller gallery loses nothing on import.
        // foundby owner 2026-06-13.
        if (Array.isArray(data.galleryUnlocked) && data.galleryUnlocked.length) {
            try { galleryUI.deserialize(data.galleryUnlocked); }
            catch(e) { console.warn('[PnO] Step 5b (gallery restore) non-fatal:', e?.message); }
        }

        // 6. Restore statistics
        if (data.statistics) {
            statisticsTracker.restoreFromSave(data.statistics);
        }

        // 7. Restore character data
        if (data.characterData) {
            if (opts.setCharacterData) opts.setCharacterData(data.characterData);
        }

        // 8. Push all vars to Ink and refresh view
        variableManager.pushVariablesToInk(story);

        // 8a. Apply extra Ink variables — recovery only.
        // Normal loads already have these in inkState; the snapshot is a
        // last-resort restore for inkState=null saves.
        if (isRecovery && data.inkVars) {
            for (const [key, value] of Object.entries(data.inkVars)) {
                try { story.variablesState[key] = value; }
                catch(e) { console.warn(`[PnO] inkVar '${key}' skipped:`, e.message); }
            }
        }

        // 8a-bis. F13: drain _pendingFlags from the v4→v5 migration. The
        // migration referenced _forceSyncAllFromInk which no longer exists,
        // so old saves never got downtown_discovered. Apply now if Ink hasn't
        // already set it via story progression.
        if (data._pendingFlags) {
            for (const [k, v] of Object.entries(data._pendingFlags)) {
                try {
                    const current = story.variablesState[k];
                    if (current === false || current === 0 || current === undefined) {
                        story.variablesState[k] = v;
                    }
                } catch(e) { /* var may not exist */ }
            }
        }

        // 8a'. Backfill app-install Ink flags from localStorage for legacy saves
        // built with v0.2.4.x or earlier. The old install flow only wrote
        // localStorage 'PnO_App_<id>', so the inkVarSnapshot is empty and
        // recovery / cross-browser imports lose every app icon. Mirror them
        // into the live story now (foundby:Jürgen v0.2.4.2).
        const APP_LS_TO_VAR = {
            grindr: 'grindr_installed',
            sissysocial: 'sissysocial_installed',
            jobfinder: 'jobfinder_installed',
            hypno: 'hypno_app_installed',
            onlyfems: 'onlyfems_installed',
            dareme: 'dareme_installed',
            cheats: 'debug_console_installed',
        };
        for (const [appId, varName] of Object.entries(APP_LS_TO_VAR)) {
            try {
                if (localStorage.getItem('PnO_App_' + appId) === 'true') {
                    story.variablesState[varName] = true;
                }
            } catch(e) {}
        }

        // 8a''. Re-derive achievements from the now-authoritative game state.
        // Step 5 only ran restoreFromSave(savedArray); the milestone + gameplay
        // re-derivation helpers were dead code (no call site) despite their
        // "Call after loading a game" docstrings, so any save with a stale/empty
        // achievement array (old format, or itch.io iframe where the
        // localStorage-only achievement store was partitioned away) showed no
        // progress. Inventory (step 3) + statistics (step 6) are already
        // restored, so the gameplay checks have their deps. Silent + idempotent.
        // foundby player 0.2.7.2 ("achievements not saved correctly in progress").
        try {
            const readAch = (name) => { try { return story.variablesState[name]; } catch(e) { return undefined; } };
            achievementManager.restoreFromGameState?.(readAch, { inventoryManager, statisticsTracker });
        } catch(e) { console.warn('[PnO] Step 8a-ach (achievement re-derive) non-fatal:', e?.message); }

        // 8b. Recovery saves: jump to Apartment hub
        if (isRecovery) {
            try { story.ChoosePathString('Apartment'); }
            catch(e) { console.error('[PnO] Recovery jump to Apartment failed:', e); }
        }

        // 8b. Restore current location from save data
        // (at choice points, currentPathString is null — so we save/restore location explicitly)
        if (data.currentLocation) {
            storyManager.currentLocation = data.currentLocation;
            console.log(`[PnO] Restored location from save: ${data.currentLocation}`);
        } else {
            // Fallback: try Ink path (works when not at a choice point)
            try {
                const savedPath = story.state.currentPathString;
                if (savedPath) {
                    storyManager.currentLocation = savedPath.split('.')[0];
                    console.log(`[PnO] Restored location from Ink path: ${storyManager.currentLocation}`);
                }
            } catch(e) {}
        }

        // 8b-bis. Force a full content-view clear on load (foundby:Ларс 0.2.9 —
        // "save/load doesn't restore, either restarts or nothing changes").
        // startStory/startFromSave set _forceClearNext before their refresh, but
        // the in-game (pause-overlay) load path reaches here WITHOUT it, so
        // refreshView(false) took the scroll-append "keep old content + separator"
        // branch. When the loaded location is in the SAME _locationFamily as what's
        // on screen (the whole Prologue is one family; also same-hub reloads), the
        // mid-process family-change clear never fires either, so the stale later
        // content stayed visible and the restored position's "Continue" was merely
        // appended below. To the player the load "did nothing" — and clicking that
        // Continue advanced the freshly-loaded earlier inkState, which read as
        // "started over". Force-clear so the restored position renders alone; a
        // choice-point save with no re-emittable narration falls back to the
        // EMPTY_PANEL_RECOVERY cache replay (story.js). Clearing an already-empty
        // panel (boot-time Continue/slot-load via bootAndApplySave) is a no-op.
        storyManager._forceClearNext = true;
        storyManager.refreshView(false);

        // 8c. Restore UI mode (desktop/browser/streaming/shop) from saved ui_mode var
        tagHandler.restoreUIMode(story);

        // 8d. Re-emit cached choices into the now-active overlay.
        //
        // refreshView() synchronously runs _processStoryContent. When the
        // saved Ink state is parked at a choice point (canContinue=false),
        // the inner loop body is skipped and _createChoicesUI emits
        // STORY_CHOICES_READY *synchronously*, BEFORE we get to call
        // restoreUIMode below. At that moment computerUI/streamingUI/shopUI
        // were still inactive — main.js's STORY_CHOICES_READY handler routed
        // the choices into the default #choices div instead of the overlay's
        // own choice grid. Then the overlay activates on top, covering the
        // empty story view + hidden choices = "black screen on load" report
        // (foundby:VPS 2026-04-28, save at StreamingSites day 42).
        //
        // Re-emit STORY_CHOICES_READY now that the overlay is active so its
        // renderChoices() pulls the cached choice list into its own grid.
        if (storyManager._lastChoices && storyManager._lastChoices.length) {
            events.emit(Events.STORY_CHOICES_READY, storyManager._lastChoices);
        }

        // 9. Update UI — read current values from Ink (post-refreshView, may have changed during overnight)
        const loadedStats = {};
        for (const key of ['Energy', 'Arousal', 'Femininity', 'Submissiveness', 'Money', 'Shame']) {
            loadedStats[key] = story.variablesState[key] || 0;
        }
        loadedStats.Day = story.variablesState['Day'] || 1;
        loadedStats.TimeSlot = story.variablesState['TimeSlot'] || 0;
        loadedStats.maxEnergy = story.variablesState['max_energy'] || 100;
        statsUI.setAll(loadedStats);

        characterPanelUI.updateSlots();
        wardrobeUI.refresh?.();
        shopUI.registerItems(inventoryManager.getAllItems());

        console.log('[PnO] Incremental load complete');

        // Blur-to-sharp reveal animation after load
        const gameView = document.getElementById('screen-game-view');
        if (gameView) {
            gameView.classList.add('game-load-reveal');
            setTimeout(() => gameView.classList.remove('game-load-reveal'), 700);
        }
    } catch (e) {
        // VPS report 2026-05-22 Android "save refused to load" had no console
        // logs attached — log the stack + which Step the per-step try/catches
        // above re-threw from so the next report includes that diagnostic.
        console.error('[PnO] Incremental load failed:', e?.message || e);
        if (e?.stack) console.error('[PnO] Load failure stack:', e.stack);
        // Persistent error display (not just a toast)
        const errDiv = document.createElement('div');
        errDiv.style.cssText = 'position:fixed;top:10px;left:50%;transform:translateX(-50%);z-index:9999;background:#2a0a0a;color:#ff6666;padding:12px 20px;border-radius:8px;border:1px solid #ff3333;font:13px monospace;max-width:600px;cursor:pointer;';
        errDiv.textContent = 'Load failed: ' + (e?.message || e);
        errDiv.title = L.get('ui.click_dismiss') || 'Click to dismiss';
        errDiv.addEventListener('click', () => errDiv.remove());
        document.body.appendChild(errDiv);
    }
}
